import type { DocSchema, DocTableColumn } from './schema';

/** 양식 문서 칸 정의 — 점검 · 안전작업 · 소통 · 비상(DOC-136). */

/** 구분 하나에 항목 여럿 → 점검표 처음 줄. */
function presetRows(groups: Array<[string, string[]]>): Array<Record<string, string>> {
  return groups.flatMap(([group, items]) => items.map((item) => ({ group, item })));
}

const CHECK_RESULT = ['양호', '불량', '해당없음'];

/** 점검표 칸 — 구분 · 점검항목 · 결과 · 조치사항. */
const CHECK_COLUMNS: DocTableColumn[] = [
  { key: 'group', label: '구분', width: '14%' },
  { key: 'item', label: '점검항목' },
  { key: 'result', label: '결과', kind: 'select', options: CHECK_RESULT, width: '12%' },
  { key: 'action', label: '조치사항', width: '26%' },
];

/** 일일 점검 항목. */
const DAILY_ITEMS: Array<[string, string[]]> = [
  ['추락', ['작업발판 · 안전난간 설치 상태', '개구부 덮개 · 난간 설치 및 표시', '안전대 부착설비 설치 및 안전대 착용']],
  ['낙하 · 비래', ['자재 적치 상태 및 낙하물 방지망', '상하 동시작업 통제 및 출입 금지 구역 설정']],
  ['전도 · 붕괴', ['비계 · 동바리 고정 및 변형 여부', '굴착면 기울기 · 흙막이 상태']],
  ['감전', ['가설 분전반 누전차단기 작동 · 접지', '전선 피복 손상 및 바닥 배선 정리']],
  ['화재', ['화기작업 허가 및 화재감시자 배치', '소화기 비치 및 인화물질 격리']],
  ['장비', ['건설기계 작업계획서 및 유도자 배치', '양중기 · 줄걸이 용구 상태', '장비 작업반경 출입 통제']],
  ['보호구', ['안전모 · 안전화 착용', '작업별 보호구(보안경 · 방진마스크 등) 착용']],
  ['작업환경', ['정리정돈 및 통로 확보', '조명 · 환기 상태', '안전표지 · 경고표지 부착']],
  ['교육 · 관리', ['TBM 실시 및 작업 전 위험 공유', '신규 근로자 교육 실시']],
];

/** 주간 점검 항목. */
const WEEKLY_ITEMS: Array<[string, string[]]> = [
  ['가설구조물', ['비계 조립 · 해체 상태 및 벽이음', '거푸집 동바리 구조검토 및 설치 상태', '가설통로 · 계단 · 사다리 상태', '낙하물 방지망 · 방호선반 설치']],
  ['양중장비', ['타워크레인 · 이동식 크레인 정기 점검', '와이어로프 · 슬링 · 훅 해지장치 상태', '고소작업대 · 리프트 방호장치']],
  ['건설기계', ['장비 정기 점검표 및 운전원 자격', '후방 경보장치 · 협착 방지 조치']],
  ['전기', ['분전반 누전차단기 시험 및 접지 저항', '임시 배선 · 이동 전선 관리']],
  ['화재 · 위험물', ['소화설비 배치 및 점검 기록', '가스 용기 보관 · 전도 방지', 'MSDS 비치 및 경고표지 부착']],
  ['관리감독자', ['관리감독자 일일 점검 실시 기록', '작업계획서 · 작업허가서 작성 및 이행']],
  ['교육', ['정기 안전보건교육 실시', '특별교육 대상 작업 교육 실시', 'TBM 실시 기록']],
  ['보건', ['휴게시설 · 세면 · 화장실 관리', '폭염 · 한파 대비 조치', '근로자 건강 상태 확인']],
  ['작업환경', ['현장 정리정돈 및 자재 적치', '안전표지 · 출입 통제 설비']],
];

/** 작업허가 전 확인 항목. */
const PERMIT_ITEMS = [
  '작업 구역 설정 및 출입 통제',
  '가스농도 측정(산소 · 유해가스 · 가연성가스)',
  '환기 설비 설치 및 가동',
  '소화기 · 방화포 비치',
  '화재감시자 배치',
  '인화성 · 가연성 물질 제거 또는 격리',
  '정전 · 잠금장치 및 꼬리표(LOTO)',
  '안전대 · 구명줄 및 부착설비',
  '송기마스크 · 공기호흡기 비치',
  '감시인 배치 및 연락 수단 확보',
  '작업자 특별교육 및 작업 방법 공유',
  '보호구 착용',
];

export const FIELD_DOCS: Record<'DAILY_CHECK' | 'WEEKLY_CHECK' | 'WORK_PLAN' | 'PERMIT' | 'COUNCIL' | 'SCENARIO' | 'DRILL' | 'ACCIDENT', DocSchema> = {
  /** 일일안전보건점검 — 정해진 항목 점검표. 제목은 점검 장소. */
  DAILY_CHECK: {
    code: 'DAILY_CHECK',
    titleKey: 'area',
    sections: [
      {
        kind: 'fields',
        fields: [
          { key: 'area', label: '점검 구역', wide: true },
          { key: 'inspector', label: '점검자' },
          { key: 'checkTime', label: '점검시간', kind: 'time' },
        ],
      },
      { kind: 'table', key: 'checklist', label: '점검 항목', columns: CHECK_COLUMNS, initialRows: presetRows(DAILY_ITEMS) },
      {
        kind: 'fields',
        fields: [{ key: 'remarks', label: '특이사항', kind: 'textarea', wide: true, rows: 4 }],
      },
    ],
  },

  /** 주간안전보건점검 — 기간 · 점검표 · 지적사항. 제목은 점검 구분. */
  WEEKLY_CHECK: {
    code: 'WEEKLY_CHECK',
    titleKey: 'title',
    sections: [
      {
        kind: 'fields',
        fields: [
          { key: 'title', label: '점검명', wide: true },
          { key: 'periodFrom', label: '점검 기간(시작)', kind: 'date' },
          { key: 'periodTo', label: '점검 기간(끝)', kind: 'date' },
          { key: 'inspector', label: '점검자' },
          { key: 'participants', label: '참여자' },
        ],
      },
      { kind: 'table', key: 'checklist', label: '점검 항목', columns: CHECK_COLUMNS, initialRows: presetRows(WEEKLY_ITEMS) },
      {
        kind: 'table',
        key: 'findings',
        label: '지적사항',
        columns: [
          { key: 'content', label: '지적내용' },
          { key: 'owner', label: '조치담당', width: '14%' },
          { key: 'due', label: '조치기한', kind: 'date', width: '16%' },
          { key: 'result', label: '조치결과', width: '24%' },
        ],
      },
      {
        kind: 'fields',
        fields: [{ key: 'remarks', label: '종합 의견', kind: 'textarea', wide: true, rows: 3 }],
      },
    ],
  },

  /** 작업계획서(산업안전보건기준에 관한 규칙 제38조). 제목은 작업명 · 세부 작업. */
  WORK_PLAN: {
    code: 'WORK_PLAN',
    titleKey: 'workTitle',
    sections: [
      {
        kind: 'fields',
        fields: [
          {
            key: 'workType',
            label: '작업 종류',
            kind: 'select',
            options: [
              '타워크레인 설치 · 해체',
              '차량계 하역운반기계 사용',
              '차량계 건설기계 사용',
              '화학설비 · 부속설비 사용',
              '전기작업',
              '굴착작업',
              '터널굴착작업',
              '교량 설치 · 해체 · 변경',
              '채석작업',
              '건물 등의 해체',
              '중량물 취급',
              '궤도 작업',
              '입환작업',
            ],
          },
          { key: 'workTitle', label: '작업명', wide: true },
          { key: 'workDate', label: '작업 일자', kind: 'date' },
          { key: 'workTime', label: '작업 시간' },
          { key: 'location', label: '작업 장소', wide: true },
          { key: 'company', label: '작업 업체' },
          { key: 'supervisor', label: '작업지휘자' },
          { key: 'workers', label: '작업 인원', kind: 'number' },
        ],
      },
      {
        kind: 'table',
        key: 'equipment',
        label: '사용 장비',
        columns: [
          { key: 'name', label: '장비명' },
          { key: 'spec', label: '규격 · 용량', width: '18%' },
          { key: 'count', label: '대수', kind: 'number', width: '10%' },
          { key: 'operator', label: '운전원', width: '16%' },
          { key: 'license', label: '자격 · 면허', width: '20%' },
        ],
      },
      {
        kind: 'table',
        key: 'steps',
        label: '작업 순서 · 방법',
        columns: [
          { key: 'step', label: '단계', width: '12%' },
          { key: 'work', label: '작업내용' },
          { key: 'hazard', label: '위험요인', width: '26%' },
          { key: 'measure', label: '안전대책', width: '28%' },
        ],
      },
      {
        kind: 'fields',
        label: '운행 · 신호',
        fields: [
          { key: 'route', label: '운행경로', kind: 'textarea', wide: true, rows: 3 },
          { key: 'speedLimit', label: '제한속도' },
          { key: 'signalman', label: '신호수 · 유도자' },
          { key: 'signal', label: '신호방법', kind: 'textarea', wide: true, rows: 2 },
        ],
      },
      {
        kind: 'fields',
        label: '비상조치',
        fields: [
          { key: 'emergency', label: '비상조치 방법', kind: 'textarea', wide: true, rows: 3 },
          { key: 'emergencyContact', label: '비상연락처', wide: true },
        ],
      },
    ],
  },

  /** 작업허가서 — 허가 종류 · 허가 전 확인 · 가스측정 · 종료 확인. 제목은 작업내용. */
  PERMIT: {
    code: 'PERMIT',
    titleKey: 'workTitle',
    sections: [
      {
        kind: 'fields',
        fields: [
          {
            key: 'permitType',
            label: '허가 종류',
            kind: 'select',
            options: ['화기작업', '밀폐공간작업', '고소작업', '정전 · 전기작업', '굴착작업', '중장비작업', '방사선작업', '중량물 인양작업', '기타'],
          },
          { key: 'workTitle', label: '작업내용', wide: true },
          { key: 'location', label: '작업장소', wide: true },
          { key: 'company', label: '신청업체' },
          { key: 'applicant', label: '신청자' },
          { key: 'workDate', label: '작업 일자', kind: 'date' },
          { key: 'workers', label: '작업 인원', kind: 'number' },
          { key: 'startTime', label: '작업 시작', kind: 'time' },
          { key: 'endTime', label: '작업 종료', kind: 'time' },
          { key: 'equipment', label: '사용 장비 · 공구', wide: true },
        ],
      },
      {
        kind: 'table',
        key: 'precheck',
        label: '허가 전 확인',
        columns: [
          { key: 'item', label: '확인 항목' },
          { key: 'checked', label: '확인', kind: 'check', width: '10%' },
          { key: 'note', label: '비고', width: '30%' },
        ],
        initialRows: PERMIT_ITEMS.map((item) => ({ item })),
      },
      {
        kind: 'table',
        key: 'gas',
        label: '가스측정 결과',
        columns: [
          { key: 'time', label: '측정시각', kind: 'time', width: '14%' },
          { key: 'o2', label: 'O2(%)', width: '13%' },
          { key: 'co', label: 'CO(ppm)', width: '13%' },
          { key: 'h2s', label: 'H2S(ppm)', width: '13%' },
          { key: 'lel', label: 'LEL(%)', width: '13%' },
          { key: 'measurer', label: '측정자' },
        ],
      },
      {
        kind: 'fields',
        label: '허가',
        fields: [
          { key: 'conditions', label: '허가 조건', kind: 'textarea', wide: true, rows: 2 },
          { key: 'approver', label: '허가자' },
          { key: 'watcher', label: '감시자' },
        ],
      },
      {
        kind: 'fields',
        label: '작업 종료 확인',
        fields: [
          { key: 'closeTime', label: '종료 시각', kind: 'time' },
          { key: 'closer', label: '확인자' },
          { key: 'closeCleanup', label: '정리정돈 · 잔류물 제거', kind: 'check' },
          { key: 'closeFire', label: '잔불 · 발화 위험 확인', kind: 'check' },
          { key: 'closeRestore', label: '잠금 해제 · 설비 원상복구', kind: 'check' },
          { key: 'closeWorkers', label: '작업자 철수 확인', kind: 'check' },
          { key: 'closeNote', label: '비고', kind: 'textarea', wide: true, rows: 2 },
        ],
      },
    ],
  },

  /** 노사협의체 회의록. 제목은 회차. */
  COUNCIL: {
    code: 'COUNCIL',
    titleKey: 'session',
    sections: [
      {
        kind: 'fields',
        fields: [
          { key: 'session', label: '회차', wide: true },
          { key: 'meetingDate', label: '일시', kind: 'date' },
          { key: 'meetingTime', label: '시각', kind: 'time' },
          { key: 'place', label: '장소', wide: true },
        ],
      },
      {
        kind: 'table',
        key: 'attendees',
        label: '참석자',
        columns: [
          { key: 'side', label: '구분', kind: 'select', options: ['근로자', '사용자'], width: '16%' },
          { key: 'org', label: '소속' },
          { key: 'position', label: '직위', width: '18%' },
          { key: 'name', label: '성명', width: '20%' },
        ],
      },
      {
        kind: 'table',
        key: 'agenda',
        label: '안건',
        columns: [
          { key: 'no', label: '번호', width: '8%' },
          { key: 'topic', label: '안건' },
          { key: 'proposer', label: '제안자', width: '16%' },
        ],
      },
      {
        kind: 'fields',
        fields: [{ key: 'result', label: '협의 결과', kind: 'textarea', wide: true, rows: 6 }],
      },
      {
        kind: 'table',
        key: 'actions',
        label: '조치 사항',
        columns: [
          { key: 'action', label: '조치 내용' },
          { key: 'owner', label: '담당', width: '16%' },
          { key: 'due', label: '기한', kind: 'date', width: '16%' },
        ],
      },
      {
        kind: 'fields',
        label: '다음 회의',
        fields: [
          { key: 'nextDate', label: '일시', kind: 'date' },
          { key: 'nextPlace', label: '장소' },
        ],
      },
    ],
  },

  /** 비상 시나리오 및 대응 절차. 제목은 시나리오명. */
  SCENARIO: {
    code: 'SCENARIO',
    titleKey: 'title',
    sections: [
      {
        kind: 'fields',
        fields: [
          {
            key: 'emergencyType',
            label: '비상 유형',
            kind: 'select',
            options: ['화재', '폭발', '붕괴', '추락', '감전', '질식', '화학물질 누출', '태풍', '호우 · 침수', '지진', '폭염', '한파', '기타'],
          },
          { key: 'title', label: '시나리오명', wide: true },
          { key: 'area', label: '대상 장소 · 작업', wide: true },
          { key: 'overview', label: '시나리오 개요', kind: 'textarea', wide: true, rows: 4 },
        ],
      },
      {
        kind: 'table',
        key: 'procedure',
        label: '대응 절차',
        columns: [
          { key: 'step', label: '단계', width: '14%' },
          { key: 'action', label: '조치내용' },
          { key: 'owner', label: '담당', width: '18%' },
        ],
        initialRows: [{ step: '발견 · 전파' }, { step: '초기 대응' }, { step: '대피 · 인원 확인' }, { step: '구조 · 응급조치' }, { step: '신고 · 보고' }, { step: '복구 · 재발 방지' }],
      },
      {
        kind: 'table',
        key: 'contacts',
        label: '비상연락망',
        columns: [
          { key: 'role', label: '역할', width: '20%' },
          { key: 'org', label: '소속' },
          { key: 'name', label: '성명', width: '18%' },
          { key: 'phone', label: '연락처', width: '22%' },
        ],
        initialRows: [{ role: '현장대리인' }, { role: '안전관리자' }, { role: '관리감독자' }, { role: '소방서', phone: '119' }, { role: '경찰서', phone: '112' }, { role: '지정 병원' }],
      },
      {
        kind: 'fields',
        label: '대피',
        fields: [
          { key: 'route', label: '대피 경로', kind: 'textarea', wide: true, rows: 3 },
          { key: 'assembly', label: '집결지', wide: true },
        ],
      },
      {
        kind: 'table',
        key: 'gear',
        label: '비상 장비',
        columns: [
          { key: 'name', label: '장비명' },
          { key: 'count', label: '수량', kind: 'number', width: '12%' },
          { key: 'place', label: '비치 장소', width: '30%' },
        ],
      },
    ],
  },

  /** 비상훈련 결과 — 사진은 편집 창이 받는다. 제목은 훈련명. */
  DRILL: {
    code: 'DRILL',
    titleKey: 'title',
    sections: [
      {
        kind: 'fields',
        fields: [
          { key: 'title', label: '훈련명', wide: true },
          { key: 'drillDate', label: '훈련 일자', kind: 'date' },
          { key: 'drillTime', label: '훈련 시각', kind: 'time' },
          {
            key: 'drillType',
            label: '훈련 유형',
            kind: 'select',
            options: ['화재 대피', '붕괴', '추락 구조', '감전', '밀폐공간 구조', '화학물질 누출', '자연재해', '응급처치', '기타'],
          },
          { key: 'participants', label: '참여 인원', kind: 'number' },
          { key: 'place', label: '훈련 장소', wide: true },
          { key: 'scenario', label: '시나리오', kind: 'textarea', wide: true, rows: 3 },
        ],
      },
      {
        kind: 'table',
        key: 'timeline',
        label: '훈련 진행',
        columns: [
          { key: 'time', label: '시각', kind: 'time', width: '14%' },
          { key: 'content', label: '내용' },
          { key: 'owner', label: '담당', width: '18%' },
        ],
      },
      {
        kind: 'table',
        key: 'evaluation',
        label: '평가',
        columns: [
          { key: 'item', label: '항목' },
          { key: 'result', label: '결과', kind: 'select', options: ['우수', '보통', '미흡'], width: '12%' },
          { key: 'improve', label: '개선사항', width: '36%' },
        ],
        initialRows: [{ item: '비상 경보 · 전파' }, { item: '대피 시간 · 경로 준수' }, { item: '인원 확인' }, { item: '초기 대응 · 장비 사용' }, { item: '역할 수행' }],
      },
      {
        kind: 'fields',
        fields: [{ key: 'review', label: '총평', kind: 'textarea', wide: true, rows: 4 }],
      },
    ],
  },

  /** 사고보고서. 제목은 사고 개요. */
  ACCIDENT: {
    code: 'ACCIDENT',
    titleKey: 'title',
    sections: [
      {
        kind: 'fields',
        fields: [
          { key: 'title', label: '사고 개요', wide: true },
          { key: 'occurDate', label: '발생 일자', kind: 'date' },
          { key: 'occurTime', label: '발생 시각', kind: 'time' },
          { key: 'place', label: '발생 장소', wide: true },
          {
            key: 'accidentType',
            label: '사고 유형',
            kind: 'select',
            options: ['추락', '넘어짐', '끼임', '부딪힘', '맞음', '깔림 · 뒤집힘', '무너짐', '절단 · 베임 · 찔림', '감전', '화재', '폭발', '질식', '이상온도 접촉', '교통사고', '기타'],
          },
          { key: 'severity', label: '재해 정도', kind: 'select', options: ['사망', '중상', '경상', '아차'] },
          { key: 'work', label: '작업 내용', wide: true },
        ],
      },
      {
        kind: 'table',
        key: 'victims',
        label: '재해자',
        columns: [
          { key: 'name', label: '성명', width: '14%' },
          { key: 'org', label: '소속' },
          { key: 'trade', label: '직종', width: '14%' },
          { key: 'age', label: '나이', kind: 'number', width: '9%' },
          { key: 'part', label: '상해부위', width: '16%' },
          { key: 'degree', label: '상해정도', width: '18%' },
        ],
      },
      {
        kind: 'fields',
        fields: [
          { key: 'progress', label: '사고 경위', kind: 'textarea', wide: true, rows: 5 },
          { key: 'directCause', label: '직접 원인', kind: 'textarea', wide: true, rows: 3 },
          { key: 'indirectCause', label: '간접 원인', kind: 'textarea', wide: true, rows: 3 },
          { key: 'firstAid', label: '응급조치', kind: 'textarea', wide: true, rows: 3 },
        ],
      },
      {
        kind: 'table',
        key: 'prevention',
        label: '재발 방지대책',
        columns: [
          { key: 'measure', label: '대책' },
          { key: 'owner', label: '담당', width: '16%' },
          { key: 'due', label: '기한', kind: 'date', width: '16%' },
        ],
      },
      {
        kind: 'fields',
        label: '보고',
        fields: [
          { key: 'reportTo', label: '보고 대상', wide: true },
          { key: 'reportDate', label: '보고 일자', kind: 'date' },
          { key: 'reportTime', label: '보고 시각', kind: 'time' },
        ],
      },
    ],
  },
};
